import axios from "axios";
import {
  LoginCredentials,
  PaginatedResponse,
  User,
  Role,
  GroupedPermission,
  Permission,
  TableQueryState,
} from "../types";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4001/v1";
const LOGIN_PATH = "/omidex";

const api = axios.create({
  baseURL: API_URL,
});

const buildTableQueryParams = (
  page: number,
  pageSize: number,
  tableQuery?: TableQueryState,
) => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });

  if (tableQuery?.sort?.field) {
    params.set("sortField", tableQuery.sort.field);
    params.set("sortDirection", tableQuery.sort.direction);
  }

  Object.entries(tableQuery?.filters || {}).forEach(([key, value]) => {
    if (String(value).trim() !== "") {
      params.set(`filter_${key}`, String(value));
    }
  });

  return params;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || "";
    const isCurlRunnerRequest = requestUrl.startsWith("/curl");

    if (
      error.response &&
      error.response.status === 401 &&
      !isCurlRunnerRequest
    ) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (window.location.pathname !== LOGIN_PATH) {
        window.location.href = LOGIN_PATH;
      }
    }
    return Promise.reject(error);
  },
);

export const auth = {
  // SEJAM National ID Authentication
  getCaptcha: async () => {
    const response = await api.get("/auth/captcha");
    return response.data as {
      success: boolean;
      result: {
        id: string;
        svg: string;
      };
    };
  },
  requestOtp: async (
    identifier: string,
    personType: "private" | "legal",
    captchaId?: string,
    captchaAnswer?: string,
  ) => {
    const response = await api.post("/auth/otp/send", {
      identifier,
      personType,
      captchaId,
      captchaAnswer,
    });
    return response.data;
  },
  verifyOtp: async (
    identifier: string,
    otp: string,
    personType: "private" | "legal",
    referralCode?: string,
  ) => {
    const response = await api.post("/auth/otp/verify", {
      identifier,
      personType,
      otp,
      referralCode,
    });
    return response.data;
  },
  requestSyncOtp: async () => {
    const response = await api.post("/auth/sync/otp/send");
    return response.data as {
      success: boolean;
      message: string;
    };
  },
  verifySyncOtp: async (otp: string) => {
    const response = await api.post("/auth/sync/otp/verify", { otp });
    return response.data as {
      success: boolean;
      message: string;
      user: any;
      userInfo: any;
    };
  },
  getMe: async () => {
    const response = await api.get("/auth/me");
    return response.data as {
      succeed: boolean;
      result: User & {
        hasSejamRecord?: boolean;
        isSepahVerified?: boolean;
        sepahRegisterUrl?: string;
        userInfo?: any | null;
        role: Role & {
          permissions: Array<string | Permission>;
        };
      };
    };
  },
  impersonateUser: async (userId: string) => {
    const response = await api.post(`/auth/impersonate/${userId}`);
    return response.data as {
      message: string;
      token: string;
      expiresIn: number;
    };
  },
  consumeImpersonationLink: async (token: string) => {
    const response = await api.post("/auth/impersonate/consume", { token });
    return response.data as {
      message: string;
      token: string;
      user: User;
    };
  },

  // Legacy Telegram login (deprecated)
  login: async (credentials: LoginCredentials) => {
    const response = await api.post("/auth/login", credentials);
    return response.data;
  },

  logout: async () => {
    const response = await api.post("/auth/logout");
    return response.data;
  },
};

export const users = {
  getAll: async (
    page: number = 1,
    pageSize: number = 20,
    tableQuery?: TableQueryState,
  ) => {
    const params = buildTableQueryParams(page, pageSize, tableQuery);
    const response = await api.get<{
      users: User[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    }>(`/users?${params.toString()}`);
    return response.data;
  },
  getOne: async (id: string) => {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },
  create: async (data: Partial<User>) => {
    const response = await api.post<User>("/users", data);
    return response.data;
  },
  update: async (id: string, data: Partial<User>) => {
    const response = await api.put<User>(`/users/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    await api.delete(`/users/${id}`);
  },
};

export const roles = {
  getAll: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get<PaginatedResponse<Role>>(
      `/roles?page=${page}&pageSize=${pageSize}`,
    );
    return response.data;
  },
  getOne: async (id: string) => {
    const response = await api.get<Role>(`/roles/${id}`);
    return response.data;
  },
  create: async (data: Partial<Role>) => {
    const response = await api.post<Role>("/roles", data);
    return response.data;
  },
  update: async (id: string, data: Partial<Role>) => {
    const response = await api.put<Role>(`/roles/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    await api.delete(`/roles/${id}`);
  },
};

export const tadbir = {
  getFunds: async () => {
    const response = await api.get("/tadbir/funds");
    const data = response.data as {
      message: string;
      funds: {
        items: any[];
        pageNumber: number;
        pageSize: number;
        totalPageCount: number;
        totalItemCount: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        isFirstPage: boolean;
        isLastPage: boolean;
        firstItemOnPage: number;
        lastItemOnPage: number;
      };
    };
    return data;
  },
  getBalances: async () => {
    const response = await api.get(`/tadbir/balances`);
    return response.data as {
      message: string;
      balances: Array<{
        remainingUnit: number;
        guaranteeCount: number;
        remainingWithGuaranteeAndRequest: number;
        requestForCancellationCount: number;
        cancelRequestInWaitCount: number;
        fundSeoRegisterNo: string;
        fundName?: string;
        fundTypeTitle?: string;
      }>;
    };
  },
  getExternalRequests: async ({
    pageNumber = 1,
    pageSize = 20,
    fromDate,
    toDate,
    fundSeoRegisterNoList,
  }: {
    pageNumber?: number;
    pageSize?: number;
    fromDate?: string;
    toDate?: string;
    fundSeoRegisterNoList?: string | string[];
  } = {}) => {
    const params = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    });
    if (fromDate) {
      params.set("fromDate", fromDate);
    }
    if (toDate) {
      params.set("toDate", toDate);
    }
    if (Array.isArray(fundSeoRegisterNoList)) {
      fundSeoRegisterNoList
        .filter(Boolean)
        .forEach((value) => params.append("fundSeoRegisterNoList", value));
    } else if (fundSeoRegisterNoList) {
      params.set("fundSeoRegisterNoList", fundSeoRegisterNoList);
    }
    const response = await api.get(
      `/tadbir/external-requests?${params.toString()}`,
    );
    return response.data as {
      message: string;
      externalRequests: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getInvestCardex: async (fundSeoRegisterNo: string) => {
    const response = await api.get(
      `/tadbir/cardex?fundSeoRegisterNo=${encodeURIComponent(
        fundSeoRegisterNo,
      )}`,
    );
    return response.data as {
      message: string;
      investCardex: any;
    };
  },
  getProfile: async () => {
    const response = await api.get("/tadbir/profile");
    return response.data as {
      message: string;
      profile: {
        all: {
          totalAsset: number;
          totalProfit: number;
          totalUnits: number;
        };
        funds: Array<{
          fundRegisterCode?: string;
          fundName?: string;
          fundTypeTitle?: string;
          reInvest?: boolean;
          reInvestPercent?: number;
          nav?: {
            subscriptionNav?: number;
            cancelNav?: number;
          };
          stats: {
            totalAsset: number;
            totalProfit: number;
            totalUnits: number;
            summary: any;
          };
        }>;
      };
    };
  },
  updateReinvest: async ({
    reInvestPercent,
    FundSeoRegisterNo,
  }: {
    reInvestPercent: number;
    FundSeoRegisterNo: string;
  }) => {
    const response = await api.post("/tadbir/reinvest", {
      reInvestPercent,
      FundSeoRegisterNo,
    });
    return response.data as {
      message: string;
      payload: any;
      reInvest: boolean;
      reInvestPercent: number;
    };
  },
  getReinvestHistory: async (
    fundSeoRegisterNo: string,
    {
      page = 1,
      pageSize = 20,
      sort = "asc",
    }: { page?: number; pageSize?: number; sort?: "asc" | "desc" } = {},
  ) => {
    const params = new URLSearchParams({
      fundSeoRegisterNo,
      page: String(page),
      pageSize: String(pageSize),
      sort,
    });
    const response = await api.get(
      `/tadbir/reinvest/history?${params.toString()}`,
    );
    return response.data as {
      history: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getTurnoverAccount: async ({
    fundSeoRegisterNo,
    fromDate,
    toDate,
    pageNumber = 1,
    pageSize = 20,
    sortField,
    sortDirection,
  }: {
    fundSeoRegisterNo: string;
    fromDate?: string;
    toDate?: string;
    pageNumber?: number;
    pageSize?: number;
    sortField?: string;
    sortDirection?: "asc" | "desc";
  }) => {
    const params = new URLSearchParams({
      fundSeoRegisterNo,
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    });
    if (fromDate) {
      params.set("fromDate", fromDate);
    }
    if (toDate) {
      params.set("toDate", toDate);
    }
    if (sortField) {
      params.set("sortField", sortField);
    }
    if (sortDirection) {
      params.set("sortDirection", sortDirection);
    }
    const response = await api.get(`/tadbir/turnover?${params.toString()}`);
    return response.data as {
      message: string;
      turnoverAccount: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getDashboardCharts: async (range: "1w" | "1m" | "1y" = "1m") => {
    const response = await api.get(
      `/tadbir/dashboard-charts?range=${encodeURIComponent(range)}`,
    );
    return response.data as {
      message: string;
      range: "1w" | "1m" | "1y";
      meta: {
        fromDate: string;
        toDate: string;
      };
      balanceChart: {
        source: string;
        labels: string[];
        rawDates: string[];
        datasets: Array<{
          label: string;
          fundSeoRegisterNo: string;
          data: Array<number | null>;
        }>;
      };
      requestChart: {
        source: string;
        labels: string[];
        issuanceAmounts: number[];
        redemptionAmounts: number[];
        issuanceCounts: number[];
        redemptionCounts: number[];
      };
    };
  },
  curlLogin: async (password: string) => {
    const response = await api.post("/curl/login", { password });
    return response.data as { token: string };
  },
  getCurlPresets: async (runnerToken: string) => {
    const response = await api.get("/curl/presets", {
      headers: {
        "x-curl-runner-token": runnerToken,
      },
    });
    return response.data as {
      presets: Array<{
        id: string;
        title: string;
        category: string;
        document: string;
        command: string;
        method?: string | null;
        url?: string | null;
      }>;
    };
  },
  runCurl: async (command: string, runnerToken: string) => {
    const response = await api.post(
      "/curl/run",
      { command },
      {
        headers: {
          "x-curl-runner-token": runnerToken,
        },
      },
    );
    return response.data as {
      ok: boolean;
      command: string;
      exitCode: number | null;
      durationMs: number;
      stdout: string;
      stderr: string;
      responseBody: string;
      httpStatus: string | null;
      contentType: string | null;
      effectiveUrl: string | null;
      errorMessage?: string;
    };
  },
};

export const certificates = {
  getAll: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get(
      `/certificates?page=${page}&pageSize=${pageSize}`,
    );
    return response.data as {
      certificates: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getOne: async (id: string) => {
    const response = await api.get(`/certificates/${id}`);
    return response.data;
  },
  create: async (data: { fundSeoRegisterNo: string; fundName?: string }) => {
    const response = await api.post("/certificates", data, {
      responseType: "blob",
    });
    return response;
  },
  download: async (id: string) => {
    const response = await api.get(`/certificates/${id}/download`, {
      responseType: "blob",
    });
    return response;
  },
};

export const permissions = {
  getAll: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get<{ data: any[] }>(`/permissions`);
    // Transform to match CrudPage expected format
    return {
      permissions: response.data.data || [],
      totalData: response.data.data?.length || 0,
      currentPage: 1,
      totalPage: 1,
    };
  },
  getGrouped: async () => {
    const response = await api.get<{ data: GroupedPermission[] }>(
      `/permissions/grouped`,
    );
    return response.data;
  },
  create: async (data: {
    resource: string;
    label: string;
    description?: string;
  }) => {
    const response = await api.post("/permissions", data);
    return response.data;
  },
  update: async (
    id: string,
    data: { label: string; description?: string; resourceLabel?: string },
  ) => {
    const response = await api.put(`/permissions/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    await api.delete(`/permissions/${id}`);
  },
};

export const transactions = {
  getAll: async (
    page: number = 1,
    pageSize: number = 20,
    tableQuery?: TableQueryState,
  ) => {
    const params = buildTableQueryParams(page, pageSize, tableQuery);
    const response = await api.get(`/transactions?${params.toString()}`);
    return response.data;
  },
  getOne: async (id: string) => {
    const response = await api.get(`/transactions/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post("/transactions", data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/transactions/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/transactions/${id}`);
    return response.data;
  },
};

export const requests = {
  getAll: async (
    page: number = 1,
    pageSize: number = 20,
    tableQuery?: TableQueryState,
  ) => {
    const params = buildTableQueryParams(page, pageSize, tableQuery);
    const response = await api.get(`/requests?${params.toString()}`);
    return response.data;
  },
  getOne: async (id: string) => {
    const response = await api.get(`/requests/${id}`);
    return response.data;
  },
  getHistory: async (
    id: string,
    {
      page = 1,
      pageSize = 20,
      sort = "asc",
    }: { page?: number; pageSize?: number; sort?: "asc" | "desc" } = {},
  ) => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
      sort,
    });
    const response = await api.get(
      `/requests/${id}/history?${params.toString()}`,
    );
    return response.data as {
      history: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  reconcileTadbir: async (id: string) => {
    const response = await api.post(`/requests/${id}/reconcile-tadbir`);
    return response.data as {
      message: string;
      data: any;
    };
  },
  retryTadbirIssuance: async (id: string) => {
    const response = await api.post(`/requests/${id}/retry-tadbir-issuance`);
    return response.data as {
      message: string;
      data: any;
    };
  },
  create: async (data: any) => {
    const response = await api.post("/requests", data);
    return response.data;
  },
  createRedemption: async (data: {
    fundKey: string;
    fundId: string;
    fundSeoRegisterNo: string;
    fundName: string;
    units: number;
    unitPrice: number;
    amountRial: number;
    notes?: string;
    meta?: Record<string, any>;
  }) => {
    const response = await api.post("/requests/redemption-investor", data);
    return response.data as {
      message: string;
      data: any;
    };
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/requests/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/requests/${id}`);
    return response.data;
  },
};

export const club = {
  getMe: async () => {
    const response = await api.get("/club/me");
    return response.data as {
      message: string;
      data: {
        isPrivatePerson: boolean;
        currentPoints: number;
        successfulReferralsCount: number;
        referralCode: string;
        referralLink: string;
        latestRedemptions: any[];
      };
    };
  },
  getHistory: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get(
      `/club/history?page=${page}&pageSize=${pageSize}`,
    );
    return response.data as {
      history: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getReferrals: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get(
      `/club/referrals?page=${page}&pageSize=${pageSize}`,
    );
    return response.data as {
      referrals: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getRewards: async () => {
    const response = await api.get("/club/rewards");
    return response.data as {
      rewards: any[];
      currentPoints: number;
    };
  },
  redeemReward: async (rewardId: string) => {
    const response = await api.post(`/club/rewards/${rewardId}/redeem`);
    return response.data as {
      message: string;
      data: any;
    };
  },
};

export const clubRewardsAdmin = {
  getAll: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get(
      `/club/admin/rewards?page=${page}&pageSize=${pageSize}`,
    );
    return response.data as {
      rewards: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  create: async (data: any) => {
    const response = await api.post("/club/admin/rewards", data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/club/admin/rewards/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/club/admin/rewards/${id}`);
    return response.data;
  },
};

export const files = {
  upload: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post("/files/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data as { file: any };
  },
  download: async (id: string) => {
    const response = await api.get(`/files/${id}`, {
      responseType: "blob",
    });
    return response;
  },
};

export const paymentSlips = {
  getAll: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get(
      `/payment-slips?page=${page}&pageSize=${pageSize}`,
    );
    return response.data as {
      paymentSlips: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getOne: async (id: string) => {
    const response = await api.get(`/payment-slips/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post("/payment-slips", data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/payment-slips/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/payment-slips/${id}`);
    return response.data;
  },
};

export const banks = {
  getAll: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get(`/banks?page=${page}&pageSize=${pageSize}`);
    return response.data as {
      banks: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getOne: async (id: string) => {
    const response = await api.get(`/banks/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post("/banks", data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/banks/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/banks/${id}`);
    return response.data;
  },
};

export const paymentBatchCards = {
  getAll: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get(
      `/payment-batch-cards?page=${page}&pageSize=${pageSize}`,
    );
    return response.data as {
      paymentBatchCards: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getOne: async (id: string) => {
    const response = await api.get(`/payment-batch-cards/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post("/payment-batch-cards", data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/payment-batch-cards/${id}`);
    return response.data;
  },
  export: async (id: string) => {
    const response = await api.get(`/payment-batch-cards/${id}/export`, {
      responseType: "blob",
    });
    return response;
  },
};

export const paymentCards = {
  getAll: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get(
      `/payment-cards?page=${page}&pageSize=${pageSize}`,
    );
    return response.data as {
      paymentCards: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getOne: async (id: string) => {
    const response = await api.get(`/payment-cards/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post("/payment-cards", data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/payment-cards/${id}`);
    return response.data;
  },
  lookup: async (cardNumber: string, fundSeoRegisterNo?: string) => {
    const searchParams = new URLSearchParams();
    if (fundSeoRegisterNo) {
      searchParams.set("fundSeoRegisterNo", fundSeoRegisterNo);
    }

    const response = await api.get(
      `/payment-cards/lookup/${cardNumber}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
    );
    return response.data as {
      cardNumber: string;
      amount: number;
      fundSeoRegisterNo: string;
      fundName: string;
      status: string;
      expiresAt: string;
    };
  },
};

export const messages = {
  getHome: async () => {
    const response = await api.get("/messages/public/home");
    return response.data as { message: any | null };
  },
  getAll: async (page: number = 1, pageSize: number = 20) => {
    const response = await api.get(
      `/messages?page=${page}&pageSize=${pageSize}`,
    );
    return response.data;
  },
  getOne: async (id: string) => {
    const response = await api.get(`/messages/${id}`);
    return response.data;
  },
  markRead: async (id: string) => {
    const response = await api.post(`/messages/${id}/read`);
    return response.data as { message: string; readAt: string };
  },
  create: async (data: any) => {
    const response = await api.post("/messages", data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/messages/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/messages/${id}`);
    return response.data;
  },
};

export const news = {
  getAll: async (
    page: number = 1,
    pageSize: number = 20,
    tableQuery?: TableQueryState,
  ) => {
    const params = buildTableQueryParams(page, pageSize, tableQuery);
    const response = await api.get(`/news?${params.toString()}`);
    return response.data;
  },
  getPublic: async ({
    page = 1,
    pageSize = 20,
    category,
    isFeatured,
  }: {
    page?: number;
    pageSize?: number;
    category?: string;
    isFeatured?: boolean;
  } = {}) => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });

    if (category) params.set("category", category);
    if (typeof isFeatured === "boolean") {
      params.set("isFeatured", String(isFeatured));
    }

    const response = await api.get(`/news/public?${params.toString()}`);
    return response.data as {
      news: any[];
      totalData: number;
      currentPage: number;
      totalPage: number;
    };
  },
  getOne: async (id: string) => {
    const response = await api.get(`/news/${id}`);
    return response.data;
  },
  getPublicOne: async (category: string, slug: string, seoTitle: string) => {
    const response = await api.get(
      `/news/public/${category}/${slug}/${seoTitle}`,
    );
    return response.data as { news: any };
  },
  create: async (data: any) => {
    const response = await api.post("/news", data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/news/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/news/${id}`);
    return response.data;
  },
};

export type PaymentStartResponse = {
  ok: boolean;
  requestId: string;
  orderId: string;
  token?: string;
  receiptToken?: string;
  redirectUrl: string;
};

export type PaymentReceiptResponse = {
  success: boolean;
  title?: string;
  message?: string;
  orderId?: string;
  amountRial?: number;
  rrn?: string | null;
  card?: string | null;
  cardMask?: string | null;
  status?: string;
  time?: string;
  confirmedAt?: string | null;
  updatedAt?: string;
  paymentMethod?: string;
  fundKey?: string;
  fundName?: string;
  fundTitle?: string;
};

export type PaymentResultRequestResponse = {
  _id: string;
  trackId?: string;
  fundName?: string;
  fundSeoRegisterNo?: string;
  paymentMethod?: "gateway" | "card" | "slip";
  amountRial?: number;
  status?: string;
  operationType?: "issuance" | "redemption";
  externalSource?: string;
  externalId?: string;
  externalStatus?: string;
  externalLastSyncedAt?: string | null;
  paymentGateway?: {
    gateway?: string;
    orderId?: string;
    token?: string;
    rrn?: string | null;
    cardMask?: string | null;
    terminalNo?: number | null;
    gatewayStatus?: number | null;
  } | null;
  paymentCard?: {
    _id?: string;
    cardNumber?: string;
    amount?: number;
    status?: string;
    usedAt?: string;
    expiresAt?: string;
    cancelledAt?: string;
    batchId?: {
      _id?: string;
      title?: string;
      description?: string;
      cancelledAt?: string;
    } | null;
  } | null;
  paymentSlip?: {
    _id?: string;
    transferedAt?: string;
    amountRial?: number;
    notes?: string;
  } | null;
  transaction?: {
    _id?: string;
    gateway?: string;
    orderId?: string;
    receiptToken?: string;
    rrn?: string | null;
    cardMask?: string | null;
    status?: string;
    updatedAt?: string;
    createdAt?: string;
  } | null;
  updatedAt?: string;
  createdAt?: string;
};

export const easyPurchase = {
  startEasyPurchase: async (
    fundId: string,
    data: {
      confirm: true;
      client?: "web" | "mobile";
      fundKey: string;
      fundName?: string;
      fundSeoRegisterNo?: string;
      nationalId?: string;
      payerType?: "personal" | "corporate";
      paymentMethod?: "gateway" | "card" | "slip";
      cardNumber?: string;
      amountRial: number;
      units: number;
      unitPrice: number;
      meta?: Record<string, any>;
    },
  ) => {
    const response = await api.post<PaymentStartResponse>(
      `/payment/fund/${encodeURIComponent(fundId)}/confirm`,
      {
        ...data,
        redirect: false,
      },
    );
    return response.data;
  },
  confirmSepahPayment: async (requestId: string) => {
    const response = await api.post<{
      succeeded: boolean;
      message: string;
      data: PaymentResultRequestResponse | null;
    }>(`/payment/sepah/request/${encodeURIComponent(requestId)}/check`);
    return response.data;
  },
  submitSepahBuyOrder: async (requestId: string) => {
    const response = await api.post<{
      succeeded: boolean;
      message: string;
      data: PaymentResultRequestResponse | null;
    }>(
      `/payment/sepah/request/${encodeURIComponent(requestId)}/add-buy-order`,
    );
    return response.data;
  },
  simulateEasyPurchaseCallback: async (data: {
    orderId: string;
    token: string;
    rrn: string;
    amountRial?: number;
    terminalNo?: number;
    hashCardNumber?: string;
  }) => {
    const callbackPayload: Record<string, string> = {
      OrderId: data.orderId,
      Token: data.token,
      status: "0",
      RRN: data.rrn,
    };

    if (data.amountRial) {
      callbackPayload.Amount = String(data.amountRial);
    }

    if (data.terminalNo) {
      callbackPayload.TerminalNo = String(data.terminalNo);
    }

    if (data.hashCardNumber) {
      callbackPayload.HashCardNumber = data.hashCardNumber;
    }

    const body = new URLSearchParams(callbackPayload);
    const response = await api.post("/payment/parsian/callback", body, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    return response.data;
  },
  getEasyPurchaseReceipt: async (orderId: string, receiptToken: string) => {
    const response = await api.get<PaymentReceiptResponse>(
      `/payment/receipt/${encodeURIComponent(orderId)}?receiptToken=${encodeURIComponent(receiptToken)}`,
    );
    return response.data;
  },
};

const apiServices = {
  auth,
  users,
  roles,
  tadbir,
  permissions,
  transactions,
  requests,
  club,
  files,
  paymentSlips,
  banks,
  paymentBatchCards,
  paymentCards,
  news,
  easyPurchase,
};

export default apiServices;
